import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Search, Save, Archive, Tag, CalendarPlus, ExternalLink,
} from "lucide-react";
import DatePickerDialog from "@/components/DatePickerDialog";
import { DEFAULT_OVERLAYS_SLOT } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { BRAND_MONOGRAM } from "@/lib/brand";

const TYPE_OPTIONS = ["VT", "Ilustra", "Entrevista", "Nota", "Bloco", "Vinheta"];

const typeColor = (t) => ({
  VT: "bg-[#152D4B] text-white",
  Ilustra: "bg-amber-500 text-white",
  Entrevista: "bg-emerald-600 text-white",
  Nota: "bg-zinc-700 text-white",
  Bloco: "bg-purple-700 text-white",
  Vinheta: "bg-pink-600 text-white",
}[t] || "bg-zinc-500 text-white");

const empty = {
  title: "", type: "VT", responsible: "", presenter: "",
  approved: false, text: "",
  tarja_chapeu: "", tarja_linha1: "", tarja_linha2: "",
  overlays_url: "",
  uppercase: false, vt_duration: 0,
  tags: [],
};

export default function GavetaPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [laudas, setLaudas] = useState([]);
  const [selected, setSelected] = useState(null); // full lauda
  const [draft, setDraft] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [exhibitFor, setExhibitFor] = useState(null); // lauda id
  const saveTimer = useRef(null);

  const load = async () => {
    const r = await api.get("/gaveta");
    setLaudas(r.data);
    if (r.data.length && !selected) {
      setSelected(r.data[0]);
      setDraft(r.data[0]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const newLauda = async () => {
    const r = await api.post("/gaveta", { ...empty, title: "Nova lauda" });
    await load();
    setSelected(r.data);
    setDraft(r.data);
  };

  const persist = async (next) => {
    if (!next) return;
    setSaving(true);
    try {
      const payload = { ...next };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const r = await api.patch(`/gaveta/${next.id}`, payload);
      setSelected(r.data);
      setLaudas((prev) => {
        const map = new Map(prev.map((l) => [l.id, l]));
        map.set(r.data.id, r.data);
        return Array.from(map.values()).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
      });
    } finally {
      setSaving(false);
    }
  };

  const onDraftChange = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 500);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta lauda da gaveta?")) return;
    await api.delete(`/gaveta/${id}`);
    toast.success("Lauda removida.");
    const next = laudas.filter((l) => l.id !== id);
    setLaudas(next);
    if (selected?.id === id) {
      setSelected(next[0] || null);
      setDraft(next[0] || null);
    }
  };

  const exhibit = async (date) => {
    if (!exhibitFor) return;
    const id = exhibitFor;
    try {
      await api.post(`/gaveta/${id}/exhibit`, { date });
      toast.success(`Lauda enviada ao espelho de ${date}.`);
      const next = laudas.filter((l) => l.id !== id);
      setLaudas(next);
      if (selected?.id === id) {
        setSelected(next[0] || null);
        setDraft(next[0] || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não consegui exibir a lauda.");
    } finally {
      setExhibitFor(null);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || !draft) return;
    if (draft.tags.includes(t)) return;
    onDraftChange({ tags: [...draft.tags, t] });
    setTagInput("");
  };

  const removeTag = (t) => {
    if (!draft) return;
    onDraftChange({ tags: draft.tags.filter((x) => x !== t) });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return laudas;
    return laudas.filter((l) =>
      l.title.toLowerCase().includes(q) ||
      (l.text || "").toLowerCase().includes(q) ||
      (l.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [laudas, search]);

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm" data-testid="back-to-calendar"
              onClick={() => nav("/")}
              className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Calendário
            </Button>
            <img
              src={BRAND_MONOGRAM}
              alt="PodiUmScript" className="h-9 w-auto"
            />
            <div className="border-l border-zinc-200 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-1.5">
                <Archive className="w-3 h-3" /> Gaveta
              </div>
              <div className="font-heading text-lg font-bold tracking-tight">
                Materiais frios
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 hidden md:inline">
              {saving ? "salvando..." : <span className="inline-flex items-center gap-1"><Save className="w-3 h-3" />salvo</span>}
            </span>
            <span className="text-sm text-zinc-600 hidden md:inline">{user?.name || user?.email}</span>
            <Button
              variant="ghost" size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
              className="rounded-sm"
            >
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Left: list */}
        <aside>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500">
              {laudas.length} lauda{laudas.length !== 1 ? "s" : ""}
            </div>
            <Button
              size="sm" data-testid="new-gaveta-lauda"
              onClick={newLauda}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Nova
            </Button>
          </div>

          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="gaveta-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar título, texto ou tag..."
              className="pl-9 rounded-sm border-zinc-300 h-10"
            />
          </div>

          {filtered.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-8 text-center text-sm text-zinc-500">
              {laudas.length === 0
                ? "Sua gaveta está vazia. Clique em 'Nova' para guardar material frio."
                : "Nenhuma lauda corresponde à busca."}
            </Card>
          ) : (
            <div className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
              {filtered.map((l) => (
                <button
                  key={l.id}
                  data-testid={`gaveta-item-${l.id}`}
                  onClick={() => { setSelected(l); setDraft(l); }}
                  className={`w-full text-left p-3 border bg-white rounded-sm ${selected?.id === l.id ? "border-[#152D4B] ring-1 ring-[#152D4B]" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`${typeColor(l.type)} text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold`}>
                      {l.type}
                    </span>
                    {l.approved && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        ok
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-zinc-900 truncate">{l.title || "Sem título"}</div>
                  {l.tags?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {l.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-sm">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Right: editor */}
        <section>
          {!draft ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-16 text-center">
              <Archive className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
              <div className="font-heading text-2xl font-bold tracking-tight">Gaveta vazia</div>
              <p className="text-zinc-500 mt-2">
                Guarde aqui laudas de material frio (perfis, biografias, listas, saltos) que podem
                ser puxadas para qualquer edição.
              </p>
              <Button
                onClick={newLauda}
                className="mt-6 rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Criar primeira lauda
              </Button>
            </Card>
          ) : (
            <Card className="rounded-sm border-zinc-200 shadow-none">
              <div className="p-6 border-b border-zinc-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                    Editando na gaveta
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      data-testid="exhibit-lauda-btn"
                      onClick={() => setExhibitFor(draft.id)}
                      className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white h-8"
                    >
                      <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Exibir em...
                    </Button>
                    <button
                      data-testid="delete-gaveta-lauda"
                      onClick={() => remove(draft.id)}
                      className="text-xs text-zinc-400 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Título</Label>
                    <Input
                      data-testid="gaveta-title"
                      value={draft.title}
                      onChange={(e) => onDraftChange({ title: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B] text-base font-semibold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Tipo</Label>
                    <Select value={draft.type} onValueChange={(v) => onDraftChange({ type: v })}>
                      <SelectTrigger data-testid="gaveta-type" className="rounded-sm border-zinc-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Apresentador</Label>
                    <Input
                      value={draft.presenter}
                      onChange={(e) => onDraftChange({ presenter: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Responsável</Label>
                    <Input
                      value={draft.responsible}
                      onChange={(e) => onDraftChange({ responsible: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="gaveta-open-tarja-btn"
                      onClick={() => window.open(DEFAULT_OVERLAYS_SLOT, "_blank", "noopener,noreferrer")}
                      className="rounded-sm mt-2 h-8"
                      title="Abrir o controle da tarja no overlays.uno"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Tarja
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                      <Checkbox
                        checked={draft.approved}
                        onCheckedChange={(v) => onDraftChange({ approved: !!v })}
                        className="rounded-sm"
                      />
                      Aprovado
                    </label>
                  </div>
                </div>

                {/* Tags */}
                <div className="mt-4 space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Tags
                  </Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(draft.tags || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-700 px-2 py-1 rounded-sm">
                        {t}
                        <button
                          onClick={() => removeTag(t)}
                          className="text-zinc-400 hover:text-red-600"
                        >×</button>
                      </span>
                    ))}
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                      placeholder="+ tag e Enter"
                      className="rounded-sm border-zinc-300 h-8 w-32 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Tarja / Lower third block removed — replaced by fixed 'Tarja' button below Responsável */}

              <div className="p-6">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Texto</Label>
                  <div className="flex items-center gap-2">
                    {draft.type === "VT" && (
                      <div className="flex items-center gap-1 border border-zinc-300 rounded-sm px-2 h-8">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">VT</span>
                        <input
                          data-testid="gaveta-vt-min"
                          type="number"
                          min={0}
                          value={Math.floor((draft.vt_duration || 0) / 60)}
                          onChange={(e) => {
                            const m = Math.max(0, parseInt(e.target.value || "0", 10));
                            const s = (draft.vt_duration || 0) % 60;
                            onDraftChange({ vt_duration: m * 60 + s });
                          }}
                          className="w-10 h-6 border-0 outline-none p-0 text-sm text-right bg-transparent"
                        />
                        <span className="text-[10px] text-zinc-500">min</span>
                        <span className="text-zinc-300">:</span>
                        <input
                          data-testid="gaveta-vt-sec"
                          type="number"
                          min={0}
                          max={59}
                          value={(draft.vt_duration || 0) % 60}
                          onChange={(e) => {
                            const s = Math.min(59, Math.max(0, parseInt(e.target.value || "0", 10)));
                            const m = Math.floor((draft.vt_duration || 0) / 60);
                            onDraftChange({ vt_duration: m * 60 + s });
                          }}
                          className="w-10 h-6 border-0 outline-none p-0 text-sm text-right bg-transparent"
                        />
                        <span className="text-[10px] text-zinc-500">seg</span>
                      </div>
                    )}
                    <button
                      type="button"
                      data-testid="gaveta-uppercase-toggle"
                      onClick={() => onDraftChange({ uppercase: !draft.uppercase })}
                      className={`text-[10px] uppercase tracking-wider font-bold h-8 px-2 border rounded-sm ${draft.uppercase ? "bg-[#152D4B] text-white border-[#152D4B]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                    >
                      AA
                    </button>
                  </div>
                </div>
                <Textarea
                  data-testid="gaveta-text"
                  value={draft.text}
                  onChange={(e) => onDraftChange({ text: e.target.value })}
                  rows={14}
                  placeholder="Texto frio para usar em qualquer edição..."
                  className={`rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B] font-mono text-[15px] leading-relaxed resize-none ${draft.uppercase ? "uppercase" : ""}`}
                />
              </div>
            </Card>
          )}
        </section>
      </main>

      <DatePickerDialog
        open={!!exhibitFor}
        onOpenChange={(o) => { if (!o) setExhibitFor(null); }}
        title="Exibir esta lauda em..."
        description="A lauda sairá da gaveta e será adicionada ao espelho da data escolhida (o espelho é criado se ainda não existir)."
        confirmLabel="Enviar ao espelho"
        onConfirm={exhibit}
      />
    </div>
  );
}
